import React, { useState, useEffect, useRef } from "react";
import InputMask from "react-input-mask";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import { useToast } from "../../../context/toastContext";
import { useConfirm } from "../../../context/confirmContext";
import { downloadFromApi } from "../../../utils/downloadBlob";
import UserPicker from "./components/UserPicker";
import AdminSupplyChainPanel from "./components/AdminSupplyChainPanel";
import SupplyChainPartnersEditor from "../../../components/SupplyChainPartners/SupplyChainPartnersEditor";
import { buildPartnersPayload, initialRows, validatePartners } from "../../../constants/supplyChain";

const US_STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
    "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
    "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
    "VA","WA","WV","WI","WY",
];

const LICENSE_TITLES = ["owner", "manager", "employee", "buyer", "budtender"];

const EMPTY_CLIENT_FORM = {
    firstName: "", lastName: "", email: "", phoneNumber: "",
    company: "", businessType: "", industry: "",
    address: "", city: "", state: "", zipCode: "", usdaLicenseNumber: "", ein: "",
};

const EMPTY_LICENSE_FORM = {
    company: "", businessType: "", industry: "",
    address: "", city: "", state: "", zipCode: "", usdaLicenseNumber: "", ein: "",
};

const LicenseManagement = ({ userProfile }) => {
    const toast = useToast();
    const confirm = useConfirm();
    const [subTab, setSubTab] = useState("real"); // "real" | "testing"
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [expandedLicenses, setExpandedLicenses] = useState(new Set());
    const [licenseUsers, setLicenseUsers] = useState({}); // licenseNo -> "loading" | user[]
    // Supply chain for the admin create forms (same rules as registration — required).
    const [clientPartners, setClientPartners] = useState([]);
    const [clientPartnerErrors, setClientPartnerErrors] = useState({});
    const [licensePartners, setLicensePartners] = useState([]);
    const [licensePartnerErrors, setLicensePartnerErrors] = useState({});
    const [selectedUsers, setSelectedUsers] = useState({}); // licenseNo -> Set(userId)
    const [colWidths, setColWidths] = useState([20, 12, 12, 9, 18, 9, 9, 11]);
    const tableRef = useRef(null);

    const [showCreateClient, setShowCreateClient] = useState(false);
    const [clientForm, setClientForm] = useState({ ...EMPTY_CLIENT_FORM });
    const [clientSubmitting, setClientSubmitting] = useState(false);
    const [clientMsg, setClientMsg] = useState({ type: "", text: "" });

    const [showCreateLicense, setShowCreateLicense] = useState(false);
    const [licenseForm, setLicenseForm] = useState({ ...EMPTY_LICENSE_FORM });
    const [licenseSubmitting, setLicenseSubmitting] = useState(false);
    const [licenseMsg, setLicenseMsg] = useState({ type: "", text: "" });

    const [editingLicenseNo, setEditingLicenseNo] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editSubmitting, setEditSubmitting] = useState(false);

    const [transferLicenseNo, setTransferLicenseNo] = useState(null);
    const [transferTarget, setTransferTarget] = useState(null);
    const [transferSubmitting, setTransferSubmitting] = useState(false);

    const [attachingTo, setAttachingTo] = useState(null); // licenseNo currently showing the attach picker
    const [attachTitle, setAttachTitle] = useState("employee");

    const fetchLicenses = () => {
        setLoading(true);
        setExpandedLicenses(new Set());
        setLicenseUsers({});
        apiClient
            .get(`/admin/licenses${subTab === "testing" ? "?testing=true" : ""}`)
            .then((res) => setLicenses(res.data))
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchLicenses();
    }, [subTab]);

    const filtered = licenses.filter((l) => {
        const q = search.toLowerCase();
        return (
            l.company?.toLowerCase().includes(q) ||
            l.licenseNo?.toLowerCase().includes(q) ||
            l.industry?.toLowerCase().includes(q) ||
            l.city?.toLowerCase().includes(q) ||
            l.state?.toLowerCase().includes(q)
        );
    });

    const toggleLicense = (licenseNo) => {
        const isExpanding = !expandedLicenses.has(licenseNo);
        setExpandedLicenses((prev) => {
            const next = new Set(prev);
            isExpanding ? next.add(licenseNo) : next.delete(licenseNo);
            return next;
        });
        if (isExpanding && !licenseUsers[licenseNo]) {
            setLicenseUsers((p) => ({ ...p, [licenseNo]: "loading" }));
            apiClient
                .get(`/admin/licenses/${licenseNo}/users`)
                .then((res) => setLicenseUsers((p) => ({ ...p, [licenseNo]: res.data })))
                .catch(() => setLicenseUsers((p) => ({ ...p, [licenseNo]: [] })));
        }
    };

    const startResize = (index) => (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidths = [...colWidths];
        const tableWidth = tableRef.current?.offsetWidth || 1000;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        const onMove = (moveE) => {
            const delta = ((moveE.clientX - startX) / tableWidth) * 100;
            const next = [...startWidths];
            next[index] = Math.max(4, startWidths[index] + delta);
            next[index + 1] = Math.max(4, startWidths[index + 1] - delta);
            setColWidths(next);
        };
        const onUp = () => {
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const updateField = (field) => (e) => {
        const value = e.target.value;
        setClientForm((p) => ({ ...p, [field]: value }));
        if (field === "businessType") {
            setClientPartners(initialRows(value));
            setClientPartnerErrors({});
        }
    };

    const handleCreateClient = async (e) => {
        e.preventDefault();
        setClientMsg({ type: "", text: "" });

        const phoneDigits = clientForm.phoneNumber.replace(/\D/g, "");
        if (phoneDigits.length < 10) {
            setClientMsg({ type: "error", text: "Phone number must be at least 10 digits." });
            return;
        }
        if (!/^\d{5}(-\d{4})?$/.test(clientForm.zipCode)) {
            setClientMsg({ type: "error", text: "Zip code must be 5 digits (or 5+4 format)." });
            return;
        }
        const partnerErrors = validatePartners(clientForm.businessType, clientPartners, "");
        delete partnerErrors.operationType; // admin forms don't collect operation type
        setClientPartnerErrors(partnerErrors);
        if (Object.keys(partnerErrors).length) {
            setClientMsg({ type: "error", text: "Complete the supply chain: every required list needs at least one business." });
            return;
        }
        setClientSubmitting(true);
        try {
            const payload = { ...clientForm, phoneNumber: phoneDigits, partners: buildPartnersPayload(clientForm.businessType, clientPartners) };
            const res = await apiClient.post("/admin/create-client", payload);
            const emailNote = res.data.emailSent ? "Onboarding email sent." : "Client created but onboarding email failed to send.";
            toast.success(`Client "${clientForm.company}" created (license ${res.data.licenseNo}). ${emailNote}`);
            setClientForm({ ...EMPTY_CLIENT_FORM });
            setClientPartners([]);
            setShowCreateClient(false);
            setClientMsg({ type: "", text: "" });
            fetchLicenses();
        } catch (err) {
            const errMsg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || "Failed to create client";
            setClientMsg({ type: "error", text: errMsg });
        } finally {
            setClientSubmitting(false);
        }
    };

    const updateLicenseFormField = (field) => (e) => {
        const value = e.target.value;
        setLicenseForm((p) => ({ ...p, [field]: value }));
        if (field === "businessType") {
            setLicensePartners(initialRows(value));
            setLicensePartnerErrors({});
        }
    };

    const handleCreateLicenseOnly = async (e) => {
        e.preventDefault();
        setLicenseMsg({ type: "", text: "" });
        if (!/^\d{5}(-\d{4})?$/.test(licenseForm.zipCode)) {
            setLicenseMsg({ type: "error", text: "Zip code must be 5 digits (or 5+4 format)." });
            return;
        }
        const partnerErrors = validatePartners(licenseForm.businessType, licensePartners, "");
        delete partnerErrors.operationType;
        setLicensePartnerErrors(partnerErrors);
        if (Object.keys(partnerErrors).length) {
            setLicenseMsg({ type: "error", text: "Complete the supply chain: every required list needs at least one business." });
            return;
        }
        setLicenseSubmitting(true);
        try {
            const res = await apiClient.post("/admin/licenses", {
                ...licenseForm,
                partners: buildPartnersPayload(licenseForm.businessType, licensePartners),
            });
            toast.success(`License "${res.data.licenseNo}" created.`);
            setLicenseForm({ ...EMPTY_LICENSE_FORM });
            setLicensePartners([]);
            setShowCreateLicense(false);
            fetchLicenses();
        } catch (err) {
            const errMsg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || "Failed to create license";
            setLicenseMsg({ type: "error", text: errMsg });
        } finally {
            setLicenseSubmitting(false);
        }
    };

    const startEditLicense = (l) => {
        setEditingLicenseNo(l.licenseNo);
        setEditForm({
            company: l.company || "", industry: l.industry || "", profile: l.profile || "",
            address: l.address || "", city: l.city || "", state: l.state || "", zipCode: l.zipCode || "",
        });
    };

    const handleSaveEdit = async (licenseNo) => {
        setEditSubmitting(true);
        try {
            await apiClient.put(`/admin/licenses/${licenseNo}`, editForm);
            toast.success("License updated.");
            setEditingLicenseNo(null);
            fetchLicenses();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update license");
        } finally {
            setEditSubmitting(false);
        }
    };

    const handleTransferOwner = async () => {
        if (!transferTarget) return;
        setTransferSubmitting(true);
        try {
            await apiClient.post(`/admin/licenses/${transferLicenseNo}/transfer-owner`, {
                newOwnerUserId: transferTarget.userId,
            });
            toast.success(`Ownership of ${transferLicenseNo} transferred to ${transferTarget.firstName} ${transferTarget.lastName}.`);
            apiClient
                .get(`/admin/licenses/${transferLicenseNo}/users`)
                .then((res) => setLicenseUsers((p) => ({ ...p, [transferLicenseNo]: res.data })));
            setTransferLicenseNo(null);
            setTransferTarget(null);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to transfer ownership");
        } finally {
            setTransferSubmitting(false);
        }
    };

    const handleAttachUser = async (licenseNo, user) => {
        try {
            await apiClient.post(`/admin/users/${user.userId}/licenses`, { licenseNo, title: attachTitle });
            toast.success(`${user.firstName} ${user.lastName} attached to ${licenseNo}.`);
            setAttachingTo(null);
            apiClient.get(`/admin/licenses/${licenseNo}/users`).then((res) => setLicenseUsers((p) => ({ ...p, [licenseNo]: res.data })));
            fetchLicenses();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to attach user");
        }
    };

    const handleChangeRole = async (licenseNo, userId, title) => {
        try {
            await apiClient.put(`/admin/users/${userId}/license-role`, { licenseNo, title });
            setLicenseUsers((p) => ({
                ...p,
                [licenseNo]: p[licenseNo].map((u) => (u.userId === userId ? { ...u, userRole: title } : u)),
            }));
            toast.success("Role updated.");
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update role");
        }
    };

    const handleDetachUser = async (licenseNo, user) => {
        const ok = await confirm({
            title: "Remove User From License",
            message: `Remove ${user.firstName} ${user.lastName} from ${licenseNo}?`,
            confirmLabel: "Remove",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await apiClient.delete(`/admin/users/${user.userId}/licenses/${licenseNo}`);
            setLicenseUsers((p) => ({ ...p, [licenseNo]: p[licenseNo].filter((u) => u.userId !== user.userId) }));
            fetchLicenses();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to remove user");
        }
    };

    const toggleUserSelected = (licenseNo, userId) => {
        setSelectedUsers((prev) => {
            const set = new Set(prev[licenseNo] || []);
            set.has(userId) ? set.delete(userId) : set.add(userId);
            return { ...prev, [licenseNo]: set };
        });
    };

    const handleBulkDetach = async (licenseNo) => {
        const userIds = Array.from(selectedUsers[licenseNo] || []);
        if (!userIds.length) return;
        const ok = await confirm({
            title: "Detach Selected Users",
            message: `Remove ${userIds.length} user(s) from ${licenseNo}?`,
            confirmLabel: "Remove",
            variant: "danger",
        });
        if (!ok) return;
        try {
            const res = await apiClient.post("/admin/users/licenses/bulk", { operation: "detach", userIds, licenseNo });
            const failed = res.data.results.filter((r) => r.status === "skipped" || r.status === "failed");
            if (failed.length) {
                toast.error(`${failed.length} user(s) could not be removed (e.g. sole owner).`);
            } else {
                toast.success("Selected users removed.");
            }
            setSelectedUsers((p) => ({ ...p, [licenseNo]: new Set() }));
            apiClient.get(`/admin/licenses/${licenseNo}/users`).then((r) => setLicenseUsers((p) => ({ ...p, [licenseNo]: r.data })));
            fetchLicenses();
        } catch (err) {
            toast.error(err.response?.data?.error || "Bulk detach failed");
        }
    };

    const handleExportCsv = async () => {
        try {
            await downloadFromApi(
                `/admin/licenses/export.csv${subTab === "testing" ? "?testing=true" : ""}`,
                `dyc-licenses-${new Date().toISOString().slice(0, 10)}.csv`
            );
        } catch (err) {
            toast.error("Failed to export licenses");
        }
    };

    const HEADERS = ["Company", "License #", "Industry", "Type", "Location", "Users", "State", "Actions"];

    return (
        <div>
            {/* Sub-tabs */}
            <div className="admin-sub-tab-bar">
                <button
                    className={`admin-sub-tab-btn ${subTab === "real" ? "active" : ""}`}
                    onClick={() => setSubTab("real")}
                >
                    Real Licenses
                </button>
                <button
                    className={`admin-sub-tab-btn ${subTab === "testing" ? "active" : ""}`}
                    onClick={() => setSubTab("testing")}
                >
                    MyTEPI Testing
                </button>
            </div>

            {/* Search */}
            <div className="admin-search-bar">
                <input
                    className="admin-search-input"
                    placeholder="Search company, license, industry, city, state…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <span className="admin-search-count">{filtered.length} license{filtered.length !== 1 ? "s" : ""}</span>
                <button className="admin-action-btn" onClick={handleExportCsv}>
                    Export CSV
                </button>
                {userProfile === "superadmin" && (
                    <>
                        <button className="admin-action-btn superadmin-only" onClick={() => setShowCreateClient(true)}>
                            + New Client
                        </button>
                        <button className="admin-action-btn superadmin-only" onClick={() => setShowCreateLicense(true)}>
                            + New License Only
                        </button>
                    </>
                )}
            </div>

            {/* Create License-only form */}
            {showCreateLicense && (
                <div className="admin-create-form-panel" style={{ padding: "var(--space-5)", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg-secondary)" }}>
                    <h3 className="admin-create-title">
                        Create License <span className="admin-role-tag superadmin">Superadmin</span>
                        <span className="admin-license-meta" style={{ display: "block", fontWeight: 400 }}>
                            No user account is created — attach an owner afterward from the license's user panel.
                        </span>
                    </h3>
                    <form onSubmit={handleCreateLicenseOnly}>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">Company Name</label>
                                <input className="admin-form-input" required value={licenseForm.company} onChange={updateLicenseFormField("company")} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">Business Type</label>
                                <select className="admin-form-input" required value={licenseForm.businessType} onChange={updateLicenseFormField("businessType")}>
                                    <option value="">Select type…</option>
                                    <option value="wholesaler">Wholesaler</option>
                                    <option value="cultivator">Cultivator</option>
                                    <option value="retailer">Retailer</option>
                                    <option value="manufacturer">Manufacturer / Processor</option>
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Industry</label>
                                <select className="admin-form-input" required value={licenseForm.industry} onChange={updateLicenseFormField("industry")}>
                                    <option value="">Select industry…</option>
                                    <option value="Rec / Adult Use">Rec / Adult Use</option>
                                    <option value="Medical">Medical</option>
                                    <option value="Hemp">Hemp</option>
                                </select>
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group" style={{ flex: "1 1 100%" }}>
                                <label className="admin-form-label">Street Address</label>
                                <input className="admin-form-input" required value={licenseForm.address} onChange={updateLicenseFormField("address")} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">City</label>
                                <input className="admin-form-input" required value={licenseForm.city} onChange={updateLicenseFormField("city")} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">State</label>
                                <select className="admin-form-input" required value={licenseForm.state} onChange={updateLicenseFormField("state")}>
                                    <option value="">Select state…</option>
                                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Zip Code</label>
                                <input className="admin-form-input" required placeholder="00000" value={licenseForm.zipCode} onChange={updateLicenseFormField("zipCode")} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">EIN (optional)</label>
                                <input className="admin-form-input" placeholder="12-3456789" value={licenseForm.ein} onChange={updateLicenseFormField("ein")} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">USDA License Number (optional)</label>
                                <input className="admin-form-input" value={licenseForm.usdaLicenseNumber} onChange={updateLicenseFormField("usdaLicenseNumber")} />
                            </div>
                        </div>

                        {licenseForm.businessType && (
                            <div style={{ margin: "var(--space-3) 0" }}>
                                <label className="admin-form-label">Supply chain</label>
                                <SupplyChainPartnersEditor
                                    idPrefix="admin-license-sc"
                                    profile={licenseForm.businessType}
                                    rows={licensePartners}
                                    onChange={setLicensePartners}
                                    errors={licensePartnerErrors}
                                    searchMode="auth"
                                    disabled={licenseSubmitting}
                                />
                            </div>
                        )}

                        {licenseMsg.text && (
                            <p className={licenseMsg.type === "error" ? "admin-form-error" : "admin-form-success"}>{licenseMsg.text}</p>
                        )}
                        <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
                            <button type="submit" className="admin-action-btn primary" disabled={licenseSubmitting}>
                                {licenseSubmitting ? "Creating…" : "Create License"}
                            </button>
                            <button type="button" className="admin-action-btn" style={{ background: "var(--color-border)", color: "var(--color-text-primary)" }} onClick={() => { setShowCreateLicense(false); setLicenseMsg({ type: "", text: "" }); }}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Create Client form */}
            {showCreateClient && (
                <div className="admin-create-form-panel" style={{ padding: "var(--space-5)", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg-secondary)" }}>
                    <h3 className="admin-create-title">
                        Create New Client <span className="admin-role-tag superadmin">Superadmin</span>
                    </h3>
                    <form onSubmit={handleCreateClient}>
                        {/* Owner Information */}
                        <p style={{ margin: "0 0 var(--space-2)", fontWeight: 600, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Owner Information</p>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">First Name</label>
                                <input className="admin-form-input" required value={clientForm.firstName} onChange={updateField("firstName")} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Last Name</label>
                                <input className="admin-form-input" required value={clientForm.lastName} onChange={updateField("lastName")} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Email</label>
                                <input className="admin-form-input" type="email" required value={clientForm.email} onChange={updateField("email")} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Phone</label>
                                <InputMask
                                    mask="(999) 999-9999"
                                    value={clientForm.phoneNumber}
                                    onChange={updateField("phoneNumber")}
                                >
                                    {(inputProps) => <input {...inputProps} className="admin-form-input" required />}
                                </InputMask>
                            </div>
                        </div>

                        {/* License Information */}
                        <p style={{ margin: "var(--space-4) 0 var(--space-2)", fontWeight: 600, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>License Information</p>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">Company Name</label>
                                <input className="admin-form-input" required value={clientForm.company} onChange={updateField("company")} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">Business Type</label>
                                <select className="admin-form-input" required value={clientForm.businessType} onChange={updateField("businessType")}>
                                    <option value="">Select type…</option>
                                    <option value="wholesaler">Wholesaler</option>
                                    <option value="cultivator">Cultivator</option>
                                    <option value="retailer">Retailer</option>
                                    <option value="manufacturer">Manufacturer / Processor</option>
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Industry</label>
                                <select className="admin-form-input" required value={clientForm.industry} onChange={updateField("industry")}>
                                    <option value="">Select industry…</option>
                                    <option value="Rec / Adult Use">Rec / Adult Use</option>
                                    <option value="Medical">Medical</option>
                                    <option value="Hemp">Hemp</option>
                                </select>
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group" style={{ flex: "1 1 100%" }}>
                                <label className="admin-form-label">Street Address</label>
                                <input className="admin-form-input" required value={clientForm.address} onChange={updateField("address")} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">City</label>
                                <input className="admin-form-input" required value={clientForm.city} onChange={updateField("city")} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">State</label>
                                <select className="admin-form-input" required value={clientForm.state} onChange={updateField("state")}>
                                    <option value="">Select state…</option>
                                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Zip Code</label>
                                <input className="admin-form-input" required placeholder="00000" value={clientForm.zipCode} onChange={updateField("zipCode")} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">EIN (optional)</label>
                                <input className="admin-form-input" placeholder="12-3456789" value={clientForm.ein} onChange={updateField("ein")} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">USDA License Number (optional)</label>
                                <input className="admin-form-input" value={clientForm.usdaLicenseNumber} onChange={updateField("usdaLicenseNumber")} />
                            </div>
                        </div>

                        {clientForm.businessType && (
                            <div style={{ margin: "var(--space-3) 0" }}>
                                <label className="admin-form-label">Supply chain</label>
                                <SupplyChainPartnersEditor
                                    idPrefix="admin-client-sc"
                                    profile={clientForm.businessType}
                                    rows={clientPartners}
                                    onChange={setClientPartners}
                                    errors={clientPartnerErrors}
                                    searchMode="auth"
                                    disabled={clientSubmitting}
                                />
                            </div>
                        )}

                        {clientMsg.text && (
                            <p className={clientMsg.type === "error" ? "admin-form-error" : "admin-form-success"}>{clientMsg.text}</p>
                        )}
                        <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
                            <button type="submit" className="admin-action-btn primary" disabled={clientSubmitting}>
                                {clientSubmitting ? "Creating…" : "Create Client"}
                            </button>
                            <button type="button" className="admin-action-btn" style={{ background: "var(--color-border)", color: "var(--color-text-primary)" }} onClick={() => { setShowCreateClient(false); setClientMsg({ type: "", text: "" }); }}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : (
                <div className="admin-table-scroll" ref={tableRef}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                {HEADERS.map((label, i) => (
                                    <th key={label} style={{ width: `${colWidths[i]}%`, position: "relative" }}>
                                        {label}
                                        {i < HEADERS.length - 1 && (
                                            <div className="col-resize-handle" onMouseDown={startResize(i)} />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr><td colSpan={HEADERS.length} className="admin-table-empty">No licenses found.</td></tr>
                            )}
                            {filtered.map((l) => {
                                const isExpanded = expandedLicenses.has(l.licenseNo);
                                const users = licenseUsers[l.licenseNo];
                                const isEditing = editingLicenseNo === l.licenseNo;
                                const selected = selectedUsers[l.licenseNo] || new Set();
                                return (
                                    <React.Fragment key={l.licenseNo}>
                                        {/* ── License row ── */}
                                        {isEditing ? (
                                            <tr>
                                                <td className="admin-td-name">
                                                    <input className="admin-form-input" value={editForm.company} onChange={(e) => setEditForm((p) => ({ ...p, company: e.target.value }))} />
                                                </td>
                                                <td className="admin-td-secondary">{l.licenseNo}</td>
                                                <td className="admin-td-secondary">
                                                    <input className="admin-form-input" value={editForm.industry} onChange={(e) => setEditForm((p) => ({ ...p, industry: e.target.value }))} />
                                                </td>
                                                <td className="admin-td-secondary">
                                                    <input className="admin-form-input" value={editForm.profile} onChange={(e) => setEditForm((p) => ({ ...p, profile: e.target.value }))} />
                                                </td>
                                                <td className="admin-td-secondary">
                                                    <input className="admin-form-input" placeholder="City" value={editForm.city} onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))} style={{ marginBottom: 4 }} />
                                                    <input className="admin-form-input" placeholder="Address" value={editForm.address} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} />
                                                </td>
                                                <td className="admin-td-secondary">{l.userCount}</td>
                                                <td className="admin-td-secondary">
                                                    <input className="admin-form-input" value={editForm.state} onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value.toUpperCase() }))} maxLength={2} />
                                                </td>
                                                <td className="admin-td-secondary">
                                                    <div style={{ display: "flex", gap: "var(--space-2)" }}>
                                                        <button className="admin-action-btn primary" disabled={editSubmitting} onClick={() => handleSaveEdit(l.licenseNo)}>
                                                            {editSubmitting ? "Saving…" : "Save"}
                                                        </button>
                                                        <button className="admin-action-btn" onClick={() => setEditingLicenseNo(null)}>Cancel</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <td className="admin-td-name">
                                                    <span className="admin-collapse-toggle" onClick={() => toggleLicense(l.licenseNo)}>
                                                        {isExpanded ? "▼" : "▶"}
                                                    </span>
                                                    {l.company}
                                                </td>
                                                <td className="admin-td-secondary">{l.licenseNo}</td>
                                                <td className="admin-td-secondary">{l.industry}</td>
                                                <td className="admin-td-secondary">{l.profile}</td>
                                                <td className="admin-td-secondary">
                                                    {[l.city, l.state].filter(Boolean).join(", ")}
                                                    {l.address && <span className="admin-license-meta">{l.address}</span>}
                                                </td>
                                                <td className="admin-td-secondary">
                                                    <span
                                                        className="admin-license-expand-trigger"
                                                        onClick={() => toggleLicense(l.licenseNo)}
                                                    >
                                                        {l.userCount} user{l.userCount !== 1 ? "s" : ""}
                                                        <span className="admin-expand-chevron">{isExpanded ? " ▲" : " ▼"}</span>
                                                    </span>
                                                </td>
                                                <td className="admin-td-secondary">{l.state}</td>
                                                <td className="admin-td-secondary">
                                                    <div style={{ display: "flex", gap: "var(--space-2)" }}>
                                                        <button className="admin-action-btn" onClick={() => startEditLicense(l)}>Edit</button>
                                                        {userProfile === "superadmin" && (
                                                            <button
                                                                className="admin-action-btn superadmin-only"
                                                                onClick={() => { setTransferLicenseNo(l.licenseNo); setTransferTarget(null); if (!isExpanded) toggleLicense(l.licenseNo); }}
                                                            >
                                                                Transfer Owner
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {/* ── Transfer owner panel ── */}
                                        {transferLicenseNo === l.licenseNo && (
                                            <tr className="admin-detail-row">
                                                <td colSpan={HEADERS.length} className="admin-detail-cell">
                                                    <div className="admin-license-users-panel">
                                                        <p style={{ margin: "0 0 var(--space-2)" }}>
                                                            New owner: <strong>{transferTarget ? `${transferTarget.firstName} ${transferTarget.lastName}` : "none selected"}</strong>
                                                        </p>
                                                        <UserPicker onSelect={setTransferTarget} placeholder="Search users to make owner…" />
                                                        <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
                                                            <button className="admin-action-btn primary" disabled={!transferTarget || transferSubmitting} onClick={handleTransferOwner}>
                                                                {transferSubmitting ? "Transferring…" : "Confirm Transfer"}
                                                            </button>
                                                            <button className="admin-action-btn" onClick={() => { setTransferLicenseNo(null); setTransferTarget(null); }}>Cancel</button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {/* ── User panel ── */}
                                        {isExpanded && (
                                            <tr className="admin-detail-row">
                                                <td colSpan={HEADERS.length} className="admin-detail-cell">
                                                    {users === "loading" ? (
                                                        <div className="admin-license-users-loading">Loading users…</div>
                                                    ) : (
                                                        <div className="admin-license-users-panel">
                                                            {users?.length > 0 && (
                                                                <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
                                                                    <button
                                                                        className="admin-action-btn"
                                                                        disabled={selected.size === 0}
                                                                        onClick={() => handleBulkDetach(l.licenseNo)}
                                                                    >
                                                                        Detach Selected ({selected.size})
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {users?.length > 0 ? (
                                                                users.map((u) => (
                                                                    <div key={u.userId} className="admin-license-user-chip">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selected.has(u.userId)}
                                                                            onChange={() => toggleUserSelected(l.licenseNo, u.userId)}
                                                                        />
                                                                        <span className="admin-license-user-name">
                                                                            {u.firstName} {u.lastName}
                                                                        </span>
                                                                        <select
                                                                            className="admin-role-tag"
                                                                            value={u.userRole}
                                                                            onChange={(e) => handleChangeRole(l.licenseNo, u.userId, e.target.value)}
                                                                        >
                                                                            {LICENSE_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
                                                                        </select>
                                                                        <span className="admin-license-meta" style={{ display: "inline" }}>{u.email}</span>
                                                                        {!u.enabled && <span className="admin-disabled-badge">Disabled</span>}
                                                                        <span
                                                                            className="admin-collapse-toggle"
                                                                            title="Remove from license"
                                                                            onClick={() => handleDetachUser(l.licenseNo, u)}
                                                                        >
                                                                            ×
                                                                        </span>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="admin-license-users-loading">No users attached to this license.</div>
                                                            )}

                                                            {attachingTo === l.licenseNo ? (
                                                                <div style={{ marginTop: "var(--space-3)" }}>
                                                                    <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", marginBottom: "var(--space-2)" }}>
                                                                        <select className="admin-form-input" value={attachTitle} onChange={(e) => setAttachTitle(e.target.value)}>
                                                                            {LICENSE_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
                                                                        </select>
                                                                        <button className="admin-action-btn" onClick={() => setAttachingTo(null)}>Cancel</button>
                                                                    </div>
                                                                    <UserPicker
                                                                        onSelect={(u) => handleAttachUser(l.licenseNo, u)}
                                                                        excludeUserIds={(users?.length ? users : []).map((u) => u.userId)}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <button className="admin-action-btn" style={{ marginTop: "var(--space-3)" }} onClick={() => setAttachingTo(l.licenseNo)}>
                                                                    + Attach User
                                                                </button>
                                                            )}

                                                            {/* Supply chain is edited by the license's owner/admin/manager in Settings. */}
                                                            <AdminSupplyChainPanel licenseNo={l.licenseNo} />
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default LicenseManagement;
